/**
 * API Client
 * 
 * Configured Axios client for API calls.
 * Includes error handling and access denied event emission.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

/**
 * Custom event for access denied errors
 */
export const ACCESS_DENIED_EVENT = 'atlas:access-denied';

/**
 * Dispatch access denied event
 */
function dispatchAccessDenied(url?: string): void {
  const event = new CustomEvent(ACCESS_DENIED_EVENT, {
    detail: { url },
  });
  window.dispatchEvent(event);
}

/**
 * Get API base URL from runtime config or env vars
 */
function getApiUrl(): string {
  const runtimeConfig = (window as { __ATLAS_CONFIG__?: { API_URL?: string } }).__ATLAS_CONFIG__;
  
  return runtimeConfig?.API_URL || 
    (typeof import.meta !== 'undefined' ? (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL : undefined) || 
    '/api';
}

/**
 * Create configured API client
 */
export function createApiClient(baseURL?: string): AxiosInstance {
  const client = axios.create({
    baseURL: baseURL || getApiUrl(),
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Handle 403 Forbidden - emit access denied event
      if (error.response?.status === 403) {
        dispatchAccessDenied(error.config?.url);
      }
      
      // Handle 401 Unauthorized - could redirect to login
      if (error.response?.status === 401) {
        // Don't redirect if already on auth page
        if (!window.location.pathname.startsWith('/auth')) {
          // Could emit event or redirect here
        }
      }
      
      return Promise.reject(error);
    }
  );

  return client;
}

// Default client instance (singleton)
let _apiClient: AxiosInstance | null = null;

/**
 * Get the default API client instance
 */
export function getApiClient(): AxiosInstance {
  if (!_apiClient) {
    _apiClient = createApiClient();
  }
  return _apiClient;
}

// Default export for convenience
export const apiClient = {
  get instance(): AxiosInstance {
    return getApiClient();
  },
};
